import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import apiManager from '@/api/AxiosInstance';
import newApiManager from '@/api/NewAxiosInstance';
import DualFooterButton from '@/components/Common/Button/DualFooterButton';
import RadioList, { OptionType } from '@/components/Common/Input/Radio/RadioList';
import TextInputLabelUp from '@/components/Common/Input/TextInputLabelUp';
import AlertModal from '@/components/Common/Modal/AlertModal';
import ConfirmModal from '@/components/Common/Modal/ConfirmModal';
import BottomSheetPopup from '@/components/Common/Popup/BottomSheetPopup';
import MenuHeader from '@/components/Header/MenuHeader';
import { ChevronDownIcon, ChevronUpIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { BASE_TONNAGE, INDIVIDUAL, INDIVIDUAL_HIGH, INDIVIDUAL_MIDDLE, INDIVIDUAL_SMALL } from '@/const/license';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { useLicenseEnumContext } from '@/contexts/License/LicenseEnumContext';
import { useMemberContext } from '@/contexts/Member/MemberContext';
import DefaultHelmet from '@/metadatas/DefaultHelmet';
import { determineLicensePlateDescription } from '@/utils/Products/productUtils';
import { getCurrentYear } from '@/utils/common';
import { getLicenseTypeValue } from '@/utils/license/licenseUtils';
import { getTonnageErrorMessage, getYearErrorMessage, processTonnageInput, processYearInput } from '@/utils/validation';

interface LicensePurchasingInquiry {
  year: string;
  tons: string;
  licenseSalesType: string | null;
  licenseType: EnumPresenter | null;
  useClassification: EnumPresenter | null;
  // locate: EnumPresenter | null;
  [key: string]: any;
}

const LicensePurchasingInquiry = () => {
  const isClicked = useRef<boolean>(false);
  const { member } = useMemberContext();
  const navigate = useNavigate();
  const [isShow, setIsShow] = useState(false);
  const [title, setTitle] = useState('');
  const [optionData, setOptionData] = useState<OptionType[]>([]);
  const { licenseEnum, setLicenseEnum } = useLicenseEnumContext();
  const [descriptionOfMaxTons, setDescriptionOfMaxTons] = useState(BASE_TONNAGE);

  const { showToast } = useToastContext();

  const yearRef = useRef<HTMLInputElement>(null);
  const tonsRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);

  const [yearError, setYearError] = useState(false);
  const [tonsError, setTonsError] = useState(false);
  const [yearErrorMsg, setYearErrorMsg] = useState('');
  const [tonsErrorMsg, setTonsErrorMsg] = useState('');

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [isFAQOpen, setIsFAQOpen] = useState(false);
  const [openFAQId, setOpenFAQId] = useState<number | null>(null);

  useEffect(() => {
    if (!licenseEnum) {
      newApiManager
        .get('/api/v1/public/license/filter-info')
        .then((response) => {
          const responseData: LicenseEnum = response.data;
          setLicenseEnum(responseData);
        })
        .catch(() => {
          showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
        });
    }
  }, []);

  useEffect(() => {
    if (!isFAQOpen) {
      setOpenFAQId(null);
    }
  }, [isFAQOpen]);

  const [licenseInquiry, setLicenseInquiry] = useState<LicensePurchasingInquiry>({
    year: '',
    tons: '',
    licenseSalesType: 'TRADE',
    licenseType: { code: '', desc: '' },
    useClassification: { code: '', desc: '' },
    // locate: { code: '', desc: '' },
  });

  const keyValueList: KeyValueListType = {
    licenseType: '번호판 종류',
    useClassification: '번호판 용도',
    // locate: '지역',
  };

  const getConfirmTitle = () => {
    const licenseType =
      licenseInquiry?.licenseSalesType === 'TRADE'
        ? getLicenseTypeValue(licenseInquiry?.licenseType, descriptionOfMaxTons)
        : '';

    return `${licenseInquiry?.tons}톤 ${licenseInquiry?.year}년 ${licenseType}을 구매 요청할까요?`;
  };

  const createLicenseInquiry = () => {
    if (isClicked.current === true) {
      return;
    }
    isClicked.current = true;

    if (licenseInquiry) {
      newApiManager
        .post('/api/v1/license-inquiry', {
          memberId: member?.id || 0,
          year: licenseInquiry?.year,
          tons: licenseInquiry?.tons,
          licenseSalesType: licenseInquiry?.licenseSalesType,
          licenseType: licenseInquiry?.licenseType?.code,
          useClassification: licenseInquiry?.useClassification?.code,
          // locate: licenseInquiry?.locate?.code,
          maxTons: descriptionOfMaxTons ? String(descriptionOfMaxTons) : null,
        })
        .then(() => {
          setIsOpenModal(true);
        })
        .catch(() => {
          showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
        })
        .finally(() => {
          isClicked.current = false;
        });
    }
  };

  const onClickSelectBox = (key: string) => {
    const arr: OptionDataType[] = [];
    setTitle(key);

    if (key === 'licenseType') {
      const tons = Number(licenseInquiry.tons);

      let baseList = licenseEnum.licenseType;
      // if (tons > 0 && tons < 1.5) {
      //   baseList = baseList.filter((item) => item.code !== 'INDIVIDUAL');
      // } else if (tons >= 1.5) {
      //   baseList = baseList.filter((item) => item.code !== 'INDIVIDUAL_DELIVERY');
      // }

      if (tons >= 0.5 && tons < 1.6) {
        baseList = baseList.filter((item) => item.code !== INDIVIDUAL_MIDDLE && item.code !== INDIVIDUAL_HIGH);
      } else if (tons >= 1.6 && tons < 17) {
        baseList = baseList.filter((item) => item.code !== INDIVIDUAL_SMALL && item.code !== INDIVIDUAL_HIGH);
      } else if (tons >= 17 && tons < 25) {
        baseList = baseList.filter((item) => item.code !== INDIVIDUAL_SMALL && item.code !== INDIVIDUAL_MIDDLE);
      }

      const newOptionData = baseList.map((item) => {
        return item;
      });

      setOptionData(newOptionData);
    } else if (key === 'useClassification') {
      setOptionData(licenseEnum.useClassification);
    } else {
      setOptionData(licenseEnum.locate);
    }
    setIsShow(true);
  };

  const onClickItem = (item: OptionDataType, key: string) => {
    setLicenseInquiry({ ...licenseInquiry, [key]: item } as LicensePurchasingInquiry);
    setTimeout(() => {
      setIsShow(false);
    }, 200);
  };

  const handleChangeYearInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmedValue = processYearInput(value);

    setLicenseInquiry({ ...licenseInquiry, [name]: trimmedValue } as LicensePurchasingInquiry);

    const errorMessage = getYearErrorMessage(trimmedValue);

    if (errorMessage) {
      setYearError(true);
      setYearErrorMsg(errorMessage);
    } else {
      setYearError(false);
      setYearErrorMsg('');
    }
  };

  const handleChangeTonsInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    const trimmedValue = processTonnageInput(value, 1);

    setLicenseInquiry({ ...licenseInquiry, [name]: trimmedValue } as LicensePurchasingInquiry);
    setDescriptionOfMaxTons(determineLicensePlateDescription(Number(value)));

    const errorMessage = getTonnageErrorMessage(trimmedValue);

    if (errorMessage) {
      setTonsError(true);
      setTonsErrorMsg(errorMessage);
    } else {
      setTonsError(false);
      setTonsErrorMsg('');
    }
  };

  const isDisabledBtn = () => {
    if (!licenseInquiry?.year || !licenseInquiry?.tons || !licenseInquiry?.licenseSalesType || yearError || tonsError) {
      return true;
    } else if (licenseInquiry?.licenseSalesType === 'TRADE') {
      return !licenseInquiry?.licenseType?.code;
    }
  };

  const scrollIntoView = (e?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e) {
      const { name } = e.target;
      if (name === 'year') {
        yearRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'tons') {
        tonsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'price') {
        priceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }
  };

  useLayoutEffect(() => {
    const detectMobileKeyboard = () => {
      scrollIntoView();
    };
    window.addEventListener('resize', detectMobileKeyboard);
    return () => {
      window.removeEventListener('resize', detectMobileKeyboard);
    };
  });

  const onChangeRadioInput = (newVal: OptionType, key: string) => {
    setLicenseInquiry({ ...licenseInquiry, [key]: newVal.code } as LicensePurchasingInquiry);
  };

  const clear = () => {
    setLicenseInquiry({
      year: '',
      tons: '',
      licenseSalesType: 'TRADE',
      licenseType: { code: '', desc: '' },
      useClassification: { code: '', desc: '' },
      // locate: { code: '', desc: '' },
    });

    setYearError(false);
    setYearErrorMsg('');
    setTonsError(false);
    setTonsErrorMsg('');
  };

  const handleCloseModal = () => {
    setIsOpenModal(false);
    navigate('/license', { state: { isForm: true } });
  };

  return (
    <>
      <DefaultHelmet page="license" />

      <MenuHeader title="번호판 구매 문의"></MenuHeader>
      <div className="w-full pt-[60px] pb-[100px] px-4 pb- text-gray-8">
        <div className="flex items-center gap-[10px] w-full p-[10px] bg-grey-2 border border-grey-5 rounded-lg shadow-rightBottom">
          <img
            src="https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/idea.png"
            alt="idea"
            className="w-8 h-8"
          />
          <div className="flex flex-col gap-1 text-[14px]">
            <p>
              <b>번호판 구매 문의시</b> 직트럭 담당자가 내용을 확인한 후 연락드립니다.
            </p>
          </div>
        </div>

        <form
          className="w-full"
          onSubmit={(e) => {
            e.preventDefault();
            if (!isDisabledBtn()) {
              setIsOpenConfirmModal(true);
            }
          }}
        >
          <div className="flex flex-col gap-[30px] mt-4">
            <TextInputLabelUp
              ref={yearRef}
              name="year"
              label="연식"
              placeholder={`연식 입력 ex) ${getCurrentYear()}`}
              value={licenseInquiry?.year || ''}
              onChange={(e) => handleChangeYearInput(e)}
              onFocus={(e) => scrollIntoView(e)}
              inputMode="numeric"
              required
              error={yearError}
              errorMsg={yearErrorMsg}
              maxLength={4}
            ></TextInputLabelUp>

            <TextInputLabelUp
              ref={tonsRef}
              name="tons"
              label="톤수"
              placeholder="톤수 입력 ex) 8.5"
              value={licenseInquiry?.tons || ''}
              onChange={(e) => handleChangeTonsInput(e)}
              onFocus={(e) => scrollIntoView(e)}
              required
              error={tonsError}
              errorMsg={tonsErrorMsg}
              suffix="t"
              maxLength={4}
            ></TextInputLabelUp>

            <div className="text-base">
              <p className="mb-2 font-semibold text-gray-8">
                거래 방식
                <span className="font-normal text-red">(필수)</span>
              </p>
              <div className="px-1">
                <RadioList
                  name="licenseSalesType"
                  list={licenseEnum.licenseSalesType}
                  value={licenseInquiry.licenseSalesType || ''}
                  onChange={(val) => {
                    onChangeRadioInput(val, 'licenseSalesType');
                  }}
                  justify="equal"
                ></RadioList>
              </div>
            </div>

            {licenseInquiry?.licenseSalesType === 'TRADE' && (
              <>
                <div onClick={() => onClickSelectBox('licenseType')}>
                  <TextInputLabelUp
                    name="licenseType"
                    label="번호판 종류"
                    placeholder="번호판 종류 선택"
                    value={getLicenseTypeValue(licenseInquiry?.licenseType, descriptionOfMaxTons)}
                    height={36}
                    type="text"
                    readOnly
                    required
                    suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
                  />
                </div>

                {/* {licenseInquiry.licenseType?.code === 'GENERAL_CARGO' && (
                  <div onClick={() => onClickSelectBox('locate')}>
                    <TextInputLabelUp
                      name="locate"
                      label="지역"
                      placeholder="지역 선택"
                      value={licenseInquiry?.locate?.desc || ''}
                      type="text"
                      readOnly
                      required
                      suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
                    ></TextInputLabelUp>
                  </div>
                )} */}
              </>
            )}
            {/* {licenseInquiry.licenseSalesType === 'RENTAL' && (
              <div onClick={() => onClickSelectBox('locate')}>
                <TextInputLabelUp
                  name="locate"
                  label="지역"
                  placeholder="지역 선택"
                  value={licenseInquiry?.locate?.desc || ''}
                  type="text"
                  readOnly
                  required
                  suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
                ></TextInputLabelUp>
              </div>
            )} */}
          </div>
        </form>

        <DualFooterButton
          leftButtonText="초기화"
          onClickLeftButton={() => {
            clear();
          }}
          rightButtonText="등록"
          disabledRight={isDisabledBtn()}
          onClickRightButton={() => {
            setIsOpenConfirmModal(true);
          }}
        ></DualFooterButton>
      </div>

      <BottomSheetPopup isOpen={isShow} onClose={() => setIsShow(false)} title={keyValueList[title]}>
        <div className="px-4 pb-8">
          <RadioList
            name="license"
            list={optionData}
            value={
              (licenseInquiry && licenseInquiry[title as keyof LicensePurchasingInquiry]?.id) ||
              (licenseInquiry && licenseInquiry[title as keyof LicensePurchasingInquiry]?.code) ||
              ''
            }
            onChange={(val) => {
              onClickItem(val, title);
            }}
            horizontal={false}
          ></RadioList>
        </div>
      </BottomSheetPopup>

      <ConfirmModal
        isOpen={isOpenConfirmModal}
        title={getConfirmTitle()}
        content={
          <>
            번호판을 구매 요청시 담당자가
            <br />
            확인 후 연락드릴 예정입니다.
          </>
        }
        rightButtonLabel="요청"
        onLeftButtonClick={() => {
          setIsOpenConfirmModal(false);
        }}
        onRightButtonClick={() => {
          setIsOpenConfirmModal(false);
          createLicenseInquiry();
        }}
      ></ConfirmModal>

      <AlertModal
        isOpen={isOpenModal}
        title="구매문의 등록 완료"
        content={
          <span>
            담당자가 확인 후<br />
            연락 드릴 예정입니다.
          </span>
        }
        onClose={handleCloseModal}
      ></AlertModal>
    </>
  );
};

export default LicensePurchasingInquiry;
